import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts';
import { OWNER_ROLES, MANAGER_ROLES, type Role } from '@rsmaids/shared/constants/roles';
import { useTranslation } from 'react-i18next';

interface BillingProtectedRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
  fallback?: React.ReactNode;
}

export const BillingProtectedRoute: React.FC<BillingProtectedRouteProps> = ({
  children,
  redirectTo = '/tabs/dashboard',
  fallback = null
}) => {
  const { t } = useTranslation();
  const { authState } = useAuth();
  const { user, isLoading } = authState;

  // Show loading state while auth is being determined
  if (isLoading) {
    return <div>{t('routing.BillingProtectedRoute.loading')}</div>; // Could be replaced with a proper loading component
  }

  // Not authenticated - redirect to dashboard
  if (!user) {
    return <Navigate to={redirectTo} replace />;
  }

  // Only owners/admins and managers can access billing
  const canAccessBilling = [...OWNER_ROLES, ...MANAGER_ROLES].includes(user.role as Role);

  if (!canAccessBilling) {
    // User doesn't have billing access
    if (fallback) {
      return <>{fallback}</>;
    }
    // Default: redirect to dashboard
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};
